/**
 * Mock Jira REST Client
 * Implements IJiraClient interface against local mock storage.
 */

import {
  ApiEnvelope,
  JiraIssue,
  JiraAcceptanceCriterion,
  JiraIssueLink,
  JiraSubtask,
} from '../../domain/types';
import { ITEMS } from '../../data/initialData';
import { toJiraIssueType } from '../../domain/mappers';
import { IJiraClient, ClientConfig } from './types';
import type { JiraConnectionAck } from '@zunelo/shared';
import {
  simulateNetwork,
  DEFAULT_CONFIG,
  isMockSourceDisconnected,
  isMockSourceRejected,
  mockRejectedCredentialMessage,
} from './mockClientUtils';

/** Explicit cross-card issue links for demo fixtures. */
const FIXTURE_ISSUE_LINKS: Record<string, JiraIssueLink[]> = {
  'DEV-3056': [
    {
      relationship: 'Relacionada',
      linkedIssueKey: 'DEV-3040',
      linkedIssueSummary: 'Carrier Popup race',
    },
    { relationship: 'Epic', linkedIssueKey: 'DEV-2900', linkedIssueSummary: 'Carrier UX epic' },
  ],
  'DEV-3040': [
    {
      relationship: 'Relacionada',
      linkedIssueKey: 'DEV-3056',
      linkedIssueSummary: 'VIN violation history',
    },
    { relationship: 'Epic', linkedIssueKey: 'DEV-2900', linkedIssueSummary: 'Carrier UX epic' },
  ],
  'DEV-3057': [
    {
      relationship: 'Relacionada',
      linkedIssueKey: 'DEV-2977',
      linkedIssueSummary: 'Sightings cache',
    },
    { relationship: 'Epic', linkedIssueKey: 'DEV-2900', linkedIssueSummary: 'Carrier UX epic' },
  ],
  'DEV-2977': [
    {
      relationship: 'Relacionada',
      linkedIssueKey: 'DEV-3057',
      linkedIssueSummary: 'Daily sightings overview',
    },
  ],
  'DEV-2802': [
    { relationship: 'Bloquea', linkedIssueKey: 'DEV-2915', linkedIssueSummary: 'Empty states' },
    { relationship: 'Epic', linkedIssueKey: 'DEV-2800', linkedIssueSummary: 'Snowflake auth epic' },
  ],
  'DEV-2915': [
    {
      relationship: 'Bloqueada por',
      linkedIssueKey: 'DEV-2802',
      linkedIssueSummary: 'Snowflake RSA',
    },
    { relationship: 'Epic', linkedIssueKey: 'DEV-2900', linkedIssueSummary: 'Carrier UX epic' },
  ],
};

/** Declared repo/project components for demo fixtures. */
const FIXTURE_COMPONENTS: Record<string, string[]> = {
  'DEV-3056': ['carrier-web', 'carrier_details_modal'],
  'DEV-3040': ['carrier-web'],
  'DEV-3057': ['carrier-web', 'sightings_api'],
  'DEV-2977': ['sightings_api'],
  'DEV-2802': ['compliance_rules_api', 'carriers_api', 'template_duckdb_file_export'],
  'DEV-2915': ['carrier-web'],
};

const FIXTURE_EPICS: Record<string, { key: string; summary: string }> = {
  'DEV-3056': { key: 'DEV-2900', summary: 'Carrier UX epic' },
  'DEV-3040': { key: 'DEV-2900', summary: 'Carrier UX epic' },
  'DEV-3057': { key: 'DEV-2900', summary: 'Carrier UX epic' },
  'DEV-2802': { key: 'DEV-2800', summary: 'Snowflake auth epic' },
  'DEV-2915': { key: 'DEV-2900', summary: 'Carrier UX epic' },
};

export class MockJiraClient implements IJiraClient {
  private issues: JiraIssue[];
  private config: ClientConfig;

  constructor(config: ClientConfig = DEFAULT_CONFIG) {
    this.config = config;

    // Hydrate from dataset
    const jiraAttentionItems = ITEMS.filter((i) => i.source === 'jira');
    this.issues = jiraAttentionItems.map((item) => {
      // Clone acceptance criteria / subtasks so per-issue mutations never bleed
      // back into the shared fixture arrays.
      const criteria: JiraAcceptanceCriterion[] = (item.criteria || []).map((c) => ({ ...c }));
      const subtasks: JiraSubtask[] = (item.subtasks || []).map((s) => ({ ...s }));
      const key = item.ticket || item.primary;
      const issueLinks = (FIXTURE_ISSUE_LINKS[key] || []).map((link) => ({ ...link }));
      const components = [...(FIXTURE_COMPONENTS[key] || [])];
      const epic = FIXTURE_EPICS[key];

      return {
        id: key,
        key,
        self: `https://jira.genlogs.io/rest/api/3/issue/${key}`,
        fields: {
          summary: item.title,
          description: item.desc || item.title,
          issueType: toJiraIssueType(typeof item.type === 'string' ? item.type : undefined),
          status: item.workflow || 'IN DEV',
          priority: 'Alta',
          labels: item.labels || [],
          components,
          parentEpicKey: epic?.key,
          parentEpicSummary: epic?.summary,
          acceptanceCriteria: criteria,
          subtasks,
          issueLinks,
          updatedAt: '2026-08-14T09:30:00.000Z',
          createdAt: '2026-08-10T08:00:00.000Z',
        },
        zuneloMeta: {
          status: item.st,
          ai: item.ai,
          next: item.next,
          refs: item.refs,
        },
      };
    });
  }

  async getIssues(): Promise<ApiEnvelope<JiraIssue[]>> {
    if (isMockSourceDisconnected('jira') || isMockSourceRejected('jira')) {
      return simulateNetwork(() => [], this.config);
    }
    return simulateNetwork(() => [...this.issues], this.config);
  }

  async getIssue(key: string): Promise<ApiEnvelope<JiraIssue | null>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      return issue ? { ...issue } : null;
    }, this.config);
  }

  async updateWorkflowState(key: string, newState: string): Promise<ApiEnvelope<JiraIssue>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      if (!issue) {
        throw new Error(`Jira issue ${key} not found`);
      }
      issue.fields.status = newState;
      issue.fields.updatedAt = new Date().toISOString();
      return { ...issue };
    }, this.config);
  }

  async toggleCriterion(key: string, criterionIndex: number): Promise<ApiEnvelope<JiraIssue>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      if (!issue || !issue.fields.acceptanceCriteria[criterionIndex]) {
        throw new Error(`Criterion at index ${criterionIndex} not found for issue ${key}`);
      }
      const c = issue.fields.acceptanceCriteria[criterionIndex];
      c.status = c.status === 'done' ? 'open' : 'done';
      c.completedAt = c.status === 'done' ? new Date().toISOString() : undefined;
      issue.fields.updatedAt = new Date().toISOString();
      return { ...issue };
    }, this.config);
  }

  async addAcceptanceCriterion(key: string, text: string): Promise<ApiEnvelope<JiraIssue>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      if (!issue) {
        throw new Error(`Jira issue ${key} not found`);
      }
      const newCriterion: JiraAcceptanceCriterion = {
        id: `c-${key}-${issue.fields.acceptanceCriteria.length + 1}`,
        text: text.trim(),
        status: 'open',
      };
      issue.fields.acceptanceCriteria.push(newCriterion);
      issue.fields.updatedAt = new Date().toISOString();
      return { ...issue };
    }, this.config);
  }

  async updateAcceptanceCriterion(
    key: string,
    criterionIndex: number,
    text: string,
  ): Promise<ApiEnvelope<JiraIssue>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      if (!issue || !issue.fields.acceptanceCriteria[criterionIndex]) {
        throw new Error(`Criterion at index ${criterionIndex} not found for issue ${key}`);
      }
      const trimmed = text.trim();
      if (!trimmed) {
        throw new Error('Criterion text cannot be empty');
      }
      issue.fields.acceptanceCriteria[criterionIndex] = {
        ...issue.fields.acceptanceCriteria[criterionIndex],
        text: trimmed,
      };
      issue.fields.updatedAt = new Date().toISOString();
      return { ...issue };
    }, this.config);
  }

  async deleteAcceptanceCriterion(
    key: string,
    criterionIndex: number,
  ): Promise<ApiEnvelope<JiraIssue>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      if (!issue || !issue.fields.acceptanceCriteria[criterionIndex]) {
        throw new Error(`Criterion at index ${criterionIndex} not found for issue ${key}`);
      }
      issue.fields.acceptanceCriteria.splice(criterionIndex, 1);
      issue.fields.updatedAt = new Date().toISOString();
      return { ...issue };
    }, this.config);
  }

  async cycleSubtask(key: string, subtaskIndex: number): Promise<ApiEnvelope<JiraIssue>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      if (!issue || !issue.fields.subtasks[subtaskIndex]) {
        throw new Error(`Subtask at index ${subtaskIndex} not found for issue ${key}`);
      }
      const s = issue.fields.subtasks[subtaskIndex];
      const nextStates: Record<JiraSubtask['status'], JiraSubtask['status']> = {
        Pendiente: 'En curso',
        'En curso': 'Hecho',
        Hecho: 'Pendiente',
        Bloqueado: 'Pendiente',
      };
      s.status = nextStates[s.status] || 'Pendiente';
      issue.fields.updatedAt = new Date().toISOString();
      return { ...issue };
    }, this.config);
  }

  async getConnection(): Promise<ApiEnvelope<JiraConnectionAck>> {
    return simulateNetwork(() => mockConnectionAck(), this.config);
  }

  async testConnection(): Promise<ApiEnvelope<JiraConnectionAck>> {
    if (isMockSourceRejected('jira')) {
      return simulateNetwork(
        () => ({
          ...mockConnectionAck(),
          status: 'error' as const,
          method: 'api_token' as const,
          error: mockRejectedCredentialMessage('jira'),
          code: 'JIRA_TOKEN_INVALID',
        }),
        this.config,
      );
    }
    return simulateNetwork(() => mockConnectionAck(), this.config);
  }

  async generateAiSummary(key: string): Promise<ApiEnvelope<JiraIssue>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      if (!issue) {
        throw new Error(`Jira issue ${key} not found`);
      }
      const summary = `Resumen IA de ${key}: ${issue.fields.summary}.`;
      const meta = {
        status: issue.zuneloMeta?.status ?? ('you' as const),
        ai: summary,
        next: issue.zuneloMeta?.next || `Actualizar estado de ${key}`,
        ...(issue.zuneloMeta?.tags ? { tags: issue.zuneloMeta.tags } : {}),
        ...(issue.zuneloMeta?.refs ? { refs: issue.zuneloMeta.refs } : {}),
      };
      issue.zuneloMeta = meta;
      issue.fields.updatedAt = new Date().toISOString();
      return {
        ...issue,
        zuneloMeta: { ...meta },
      };
    }, this.config);
  }

  async addComment(key: string, markdown: string): Promise<ApiEnvelope<unknown>> {
    return simulateNetwork(() => {
      if (!this.issues.some((issue) => issue.key === key))
        throw new Error(`Jira issue ${key} not found`);
      if (!markdown.trim()) throw new Error('Comment cannot be empty');
      return { key, markdown, commentId: `mock-comment-${key}` };
    }, this.config);
  }

  async generateCommentDraft(
    key: string,
    hint?: string,
  ): Promise<ApiEnvelope<{ draft: string; provider?: string; model?: string }>> {
    return simulateNetwork(() => {
      const issue = this.issues.find((i) => i.key === key);
      if (!issue) {
        throw new Error(`Jira issue ${key} not found`);
      }
      const idea = hint?.trim();
      const draft = idea
        ? `${idea}\n\nContexto: ${issue.fields.summary} (${issue.fields.status}).`
        : `Comentario sobre ${key}: ${issue.fields.summary}. Próximo paso: ${
            issue.zuneloMeta?.next || 'confirmar avance con el equipo'
          }.`;
      return { draft, provider: 'deterministic' };
    }, this.config);
  }
}

function mockConnectionAck(): JiraConnectionAck {
  if (isMockSourceDisconnected('jira')) {
    return {
      status: 'not_configured',
      method: 'none',
      displayName: null,
      accountEmail: null,
      siteUrl: null,
      siteName: null,
      checkedAt: new Date().toISOString(),
      error: null,
      oauthAvailable: false,
      code: 'JIRA_NOT_CONFIGURED',
    };
  }
  return {
    status: 'mock',
    method: 'mock',
    displayName: null,
    accountEmail: null,
    siteUrl: 'https://jira.mock.local',
    siteName: 'Mock workspace',
    checkedAt: new Date().toISOString(),
    error: null,
    oauthAvailable: false,
    code: null,
  };
}
